import { useState } from "react";
import { Link } from "react-router-dom";
import { getPhotoMemories, listPhotos } from "../api/endpoints";
import { mediaThumbnailUrl } from "../api/types";
import { Button, Card, EmptyState, ErrorMessage, Input, PageHeading, Spinner } from "../components/ui";
import { useAsync } from "../hooks/useAsync";

export default function PhotosPage() {
  const [page, setPage] = useState(1);
  const [type, setType] = useState("");
  const [year, setYear] = useState("");
  const { data, error, loading, reload } = useAsync(
    async () => {
      const [photos, memories] = await Promise.all([
        listPhotos({ page, type: type || undefined, year: year || undefined }),
        getPhotoMemories(),
      ]);
      return { photos, memories };
    },
    [page, type, year],
  );

  if (loading) return <Spinner />;
  if (error || !data) return <ErrorMessage error={error ?? new Error("Failed to load gallery")} onRetry={reload} />;

  return (
    <div className="space-y-6" data-testid="photos-page">
      <PageHeading title="Photo gallery" subtitle="Activity photos and same-day memories." />

      <Card className="p-4" data-testid="photos-filter-panel">
        <div className="grid gap-4 md:grid-cols-3">
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>Activity type</span>
            <Input value={type} onChange={(event) => { setType(event.target.value); setPage(1); }} placeholder="run, ride, hike" data-testid="photos-type-input" />
          </label>
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>Year</span>
            <Input type="number" value={year} onChange={(event) => { setYear(event.target.value); setPage(1); }} placeholder="2026" data-testid="photos-year-input" />
          </label>
          <div className="flex items-end gap-2">
            <Button variant="secondary" onClick={reload} data-testid="photos-refresh-button">Refresh</Button>
            <Button variant="secondary" onClick={() => { setType(""); setYear(""); setPage(1); }} data-testid="photos-clear-button">Clear</Button>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-heading-md text-gray-900 dark:text-gray-100">Memories</h2>
        {data.memories.years.length === 0 ? (
          <div className="mt-4">
            <EmptyState title="No memories today" description="When you revisit this date in future years, this section will fill itself in." testId="photos-memories-empty-state" />
          </div>
        ) : (
          <div className="mt-4 space-y-4" data-testid="photos-memories-panel">
            {data.memories.years.map((group) => (
              <Card key={group.year} className="p-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{group.year} · {group.yearsAgo} year{group.yearsAgo === 1 ? "" : "s"} ago</h3>
                <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {group.activities.map((activity) => (
                    <Link key={activity.activityId} to={`/activities/${activity.activityId}`} className="rounded-lg border border-gray-200 p-3 dark:border-[#2e2e2e]" data-testid="photos-memory-activity-link">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{activity.activityName}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{activity.activityType} · {activity.activityDate}</p>
                    </Link>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-6">
        <h2 className="text-heading-md text-gray-900 dark:text-gray-100">Gallery</h2>
        {data.photos.items.length === 0 ? (
          <div className="mt-4">
            <EmptyState title="No photos found" description="Try a different year/type filter or wait for imported media." testId="photos-gallery-empty-state" />
          </div>
        ) : (
          <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="photos-grid-panel">
            {data.photos.items.map((photo) => (
              <Link key={photo.mediaId} to={`/activities/${photo.activityId}`} className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-[#2e2e2e] dark:bg-[#1a1a1a]" data-testid="photos-card-link">
                <img src={mediaThumbnailUrl(photo.mediaId)} alt={photo.caption ?? photo.activityName} className="h-56 w-full object-cover" />
                <div className="p-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{photo.activityName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{photo.activityType} · {photo.activityDate}</p>
                  {photo.caption && <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{photo.caption}</p>}
                </div>
              </Link>
            ))}
          </div>
        )}
        <div className="mt-4 flex items-center justify-center gap-4">
          <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))} data-testid="photos-previous-button">Previous</Button>
          <span className="text-sm text-gray-500 dark:text-gray-400" data-testid="photos-page-label">Page {data.photos.page}</span>
          <Button variant="secondary" disabled={!data.photos.hasMore} onClick={() => setPage((value) => value + 1)} data-testid="photos-next-button">Next</Button>
        </div>
      </Card>
    </div>
  );
}
